// --- Core ---
import {Field, useForm} from "react-final-form";
import {JSX} from "react";
import {FormattedMessage} from "react-intl";
import {endOfDay, endOfMonth, endOfYear, formatISO, startOfDay, startOfMonth, startOfYear, subDays, subHours, subMonths, subYears} from "date-fns";
import {map} from "underscore";
// --- Material UI ---
import {Grid, MenuItem} from "@mui/material";
// --- SaniQ ---
import {BaseFilter, FilterType} from "../types";
import {CheckFilter, FilterContentProps, FilterSubheaderProps, FilterValue, FormatFilter, SerializeFilter} from "shared/filter/handlers/types";
import {DateField} from "helpers/forms/fields/DateField";
import {required} from "helpers/forms/Validation";
import {SelectField} from "helpers/forms/fields/SelectField";
import {DateTimeField} from "helpers/forms/fields/DateTimeField";
import {parseOptionalISO} from "helpers/Parser";
import {FieldListener} from "helpers/Forms";

enum DateRange {
  CurrentYear = 'curY',
  LastYear = 'lastY',
  LastMonth = 'lastM',
  CurrentMonth = 'curM',
  Last7Days = '7D',
  Last30Days = '30D',
  CustomRange = 'custom',
  Yesterday = 'yesterday',
  Today = 'today',
  Last24Hours = '24H',
  Last12Hours = '12H',
  Last6Hours = '6H'
}

interface DateRangeFilter extends BaseFilter {
  filterType: FilterType.DateRange | FilterType.TimeRange;
  value?: DateRange;
  to?: Date;
  from?: Date;
}

interface DateRangeContentProps extends FilterContentProps {
  isTime?: boolean;
}

interface DateRangeSubheaderProps extends FilterSubheaderProps {
  isTime?: boolean;
}

const DateRangeLabel = ({value}: {value: DateRange}): JSX.Element => {
  const getMessage = (key: DateRange): JSX.Element => {
    switch (key) {
      case DateRange.LastMonth:
        return (
          <FormattedMessage
            id="filter.dateRange.LastMonth"
            defaultMessage="Last month"
          />
        );
      case DateRange.CurrentMonth:
        return (
          <FormattedMessage
            id="filter.dateRange.CurrentMonth"
            defaultMessage="Current month"
          />
        );
      case DateRange.Last7Days:
        return (
          <FormattedMessage
            id="filter.dateRange.Last7Days"
            defaultMessage="Last 7 days"
          />
        );
      case DateRange.Last30Days:
        return (
          <FormattedMessage
            id="filter.dateRange.Last30Days"
            defaultMessage="Last 30 days"
          />
        );
      case DateRange.CurrentYear:
        return (
          <FormattedMessage
            id="filter.dateRange.CurrentYear"
            defaultMessage="Current year"
          />
        );
      case DateRange.LastYear:
        return (
          <FormattedMessage
            id="filter.dateRange.LastYear"
            defaultMessage="Last year"
          />
        );
      case DateRange.CustomRange:
        return (
          <FormattedMessage
            id="filter.dateRange.CustomRange"
            defaultMessage="Custom range"
          />
        );
      case DateRange.Yesterday:
        return (
          <FormattedMessage
            id="filter.dateRange.Yesterday"
            defaultMessage="Yesterday"
          />
        );
      case DateRange.Today:
        return (
          <FormattedMessage
            id="filter.dateRange.Today"
            defaultMessage="Today"
          />
        );
      case DateRange.Last24Hours:
        return (
          <FormattedMessage
            id="filter.dateRange.Last24Hours"
            defaultMessage="Last 24 hours"
          />
        );
      case DateRange.Last12Hours:
        return (
          <FormattedMessage
            id="filter.dateRange.Last12Hours"
            defaultMessage="Last 12 hours"
          />
        );
      case DateRange.Last6Hours:
        return (
          <FormattedMessage
            id="filter.dateRange.Last6Hours"
            defaultMessage="Last six hours"
          />
        );
      default:
        return (
          <FormattedMessage
            id="filter.dateRange.unknown"
            defaultMessage="Unknown range"
          />
        );
    }
  };

  return getMessage(value);
};

const getDateRangeValues = (range: DateRange) => {
  let from: Date | null = null;
  let to: Date | null = null;

  switch (range) {
    case DateRange.LastMonth:
      from = subMonths(startOfMonth(new Date()), 1);
      to = subMonths(endOfMonth(new Date()), 1);
      break;
    case DateRange.CurrentMonth:
      from = startOfMonth(new Date());
      to = endOfDay(new Date());
      break;
    case DateRange.Last7Days:
      from = subDays(startOfDay(new Date()), 7);
      to = endOfDay(new Date());
      break;
    case DateRange.Last30Days:
      from = subDays(startOfDay(new Date()), 30);
      to = endOfDay(new Date());
      break;
    case DateRange.CurrentYear:
      from = startOfYear(new Date());
      to = new Date();
      break;
    case DateRange.LastYear:
      from = subYears(startOfYear(new Date()), 1);
      to = subYears(endOfYear(new Date()), 1);
      break;
    case DateRange.Yesterday:
      from = subDays(startOfDay(new Date()),1);
      to = subDays(endOfDay(new Date()), 1);
      break;
    case DateRange.Today:
      from = startOfDay(new Date());
      to = endOfDay(new Date());
      break;
    case DateRange.Last24Hours:
      from =subHours(new Date(), 24);
      to = new Date();
      break;
    case DateRange.Last12Hours:
      from = subHours(new Date(), 12);
      to = new Date();
      break;
    case DateRange.Last6Hours:
      from = subHours(new Date(), 6);
      to = new Date();
      break;
    default:
      break;
  }

  return {from, to};
};

const DateRangeContent  = ({name, isTime = false}:DateRangeContentProps) => {
  const form = useForm();

  // Define which ranges should appear based on isTime flag
  const availableRanges = isTime ? [
    DateRange.Today,
    DateRange.Yesterday,
    DateRange.Last6Hours,
    DateRange.Last12Hours,
    DateRange.Last24Hours,
    DateRange.Last7Days,
    DateRange.Last30Days,
    DateRange.CustomRange
  ] : [
    DateRange.CurrentMonth,
    DateRange.LastMonth,
    DateRange.CurrentYear,
    DateRange.LastYear,
    DateRange.Last7Days,
    DateRange.Last30Days,
    DateRange.CustomRange
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Field<DateRange>
          name={`${name}.value`}
          component={SelectField}
          label={<FormattedMessage id="filter.dateRange" defaultMessage="Date Range"/>}
          required validate={required}
        >
          {
            map(availableRanges,
              (range) => (
                <MenuItem key={range} value={range}>
                  <DateRangeLabel value={range}/>
                </MenuItem>
              )
            )
          }
        </Field>
        <FieldListener<DateRange>
          name={`${name}.value`}
          onChange={
            (value) => {
              if (value !== DateRange.CustomRange) {
                const {from, to} = getDateRangeValues(value);

                form.batch(() => {
                  form.change(`${name}.from`, from);
                  form.change(`${name}.to`, to);
                });
              }
            }
          }
        />
      </Grid>
      <Grid item xs={6}>
        <Field<Date>
          name={`${name}.from`}
          component={isTime ? DateTimeField : DateField}
          label={<FormattedMessage id="filter.dateRange.from" defaultMessage="From"/>}
        />
      </Grid>
      <Grid item xs={6}>
        <Field<Date>
          name={`${name}.to`}
          component={isTime ? DateTimeField : DateField}
          label={<FormattedMessage id="filter.dateRange.to" defaultMessage="To"/>}
        />
      </Grid>
    </Grid>
  );
};

const DateRangeSubheader = ({name, isTime = false}: DateRangeSubheaderProps) => (
  <Field name={name}>
    {
      ({input: {value: {to, from, value}}}) => {
        if (value === DateRange.CustomRange) {
          return isTime ? (
            <FormattedMessage
              id="filter.dateTimeRange.subHeader"
              defaultMessage="From {from, date, short} at {from, time, short} to {to, date, short} at {to, time, short}"
              values={{from, to}}
            />
          ) : (
            <FormattedMessage
              id="filter.dateRange.subHeader"
              defaultMessage="From {from, date, short} to {to, date, short}"
              values={{from, to}}
            />
          );
        } else {
          return <DateRangeLabel value={value}/>;
        }
      }
    }
  </Field>
);

const formatDateRangeFilter: FormatFilter<DateRangeFilter> = ({id, from, to}) => {
  const filters = [];

  if (from) {
    filters.push(`${id}[from]=${encodeURIComponent(formatISO(from))}`);
  }

  if (to) {
    filters.push(`${id}[to]=${encodeURIComponent(formatISO(to))}`);
  }

  return filters.join('&');
};

const serializeDateRangeFilter: SerializeFilter<DateRangeFilter> = ({id, type, filterType, value, from, to}): FilterValue => ({
  id,
  type,
  filterType,
  value,
  from: from ? formatISO(from): undefined,
  to: to ? formatISO(to): undefined
});

const checkDateRangeFilter: CheckFilter<DateRangeFilter> = (filter) => {
  const {value, from, to} = filter;

  if (value !== DateRange.CustomRange) {
    const {from: newFrom, to: newTo} = getDateRangeValues(value);
    filter.from = newFrom;
    filter.to = newTo;
  } else {
    filter.from = parseOptionalISO(from);
    filter.to = parseOptionalISO(to);
  }

  try {
    if ((filter.from && isNaN(filter.from.getTime())) || (filter.to && isNaN(filter.to.getTime()))) {
      throw new TypeError('Filter contains an invalid date');
    }

    if (filter.from && filter.to && filter.from > filter.to) {
      throw new TypeError('Filter "from" date cannot be after "to" date');
    }
  } catch (error) {
    return undefined;
  }

  return filter;
};

export {
  serializeDateRangeFilter,
  checkDateRangeFilter,
  DateRange,
  DateRangeFilter,
  DateRangeContent,
  DateRangeSubheader,
  formatDateRangeFilter,
  getDateRangeValues
};
